import Phaser from 'phaser';
import { FONT_FAMILY, GAME_WIDTH, getGameHeight } from '../config/constants';
import { LEVEL_METADATA, LEVEL_COUNT } from '../levels/imageLevels';
import { crispText } from '../ui/textUtils';
import { createButton } from '../ui/Button';
import { launchGameScene } from '../types/GameSceneLaunchData';
import { fadeToScene } from '../ui/sceneTransition';
import { markLevelCompleted } from '../systems/progressStore';

/** Data passed from GameScene to GameOverScene. */
interface GameOverData {
  won: boolean;
  level: number;
  reason?: string;
  /** Raw decision log lines from GameScene (for LOG download button). */
  decisionLog?: string[];
  /** Level name for the decision log header. */
  levelName?: string;
  /** Pre-formatted bot trace text to auto-download once overlay is shown. */
  traceText?: string;
  /** Queue lane override from GameScene (persists across retry). */
  queueOverride?: number;
}

export class GameOverScene extends Phaser.Scene {
  /** Container holding the full overlay UI (background + buttons + text). */
  private overlayContainer!: Phaser.GameObjects.Container;
  /** Small pill button shown when overlay is dismissed. */
  private showResultsPill!: Phaser.GameObjects.Container;
  /** Whether the overlay is currently visible. */
  private overlayVisible = true;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  create(data: GameOverData): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = getGameHeight() / 2;
    const gameH = getGameHeight();

    // Save progression on win (uuid-keyed — survives level reorders/culls)
    if (data.won) {
      markLevelCompleted(data.level);
    }

    // ── Overlay container (everything that gets toggled) ────────────────
    this.overlayContainer = this.add.container(0, 0);

    // ── Semi-transparent dark backdrop ──────────────────────────────────
    const backdrop = this.add.graphics();
    backdrop.fillStyle(0x000000, 0.75);
    backdrop.fillRect(0, 0, GAME_WIDTH, gameH);
    backdrop.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, GAME_WIDTH, gameH),
      Phaser.Geom.Rectangle.Contains,
    );
    this.overlayContainer.add(backdrop);

    // ── Celebration / defeat particles ───────────────────────────────────
    if (data.won) {
      const p = this.add.particles(centerX, gameH + 20, 'particle', {
        x: { min: -GAME_WIDTH / 2, max: GAME_WIDTH / 2 },
        speedY: { min: -80, max: -30 },
        speedX: { min: -20, max: 20 },
        scale: { start: 0.6, end: 0 },
        alpha: { start: 0.8, end: 0 },
        lifespan: 3000,
        frequency: 60,
        tint: [0x2ecc71, 0x3498db, 0xf1c40f, 0xe74c3c],
        blendMode: Phaser.BlendModes.ADD,
      });
      this.overlayContainer.add(p);
    } else {
      const p = this.add.particles(centerX, -20, 'particle', {
        x: { min: -GAME_WIDTH / 2, max: GAME_WIDTH / 2 },
        speedY: { min: 15, max: 40 },
        speedX: { min: -10, max: 10 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.2, end: 0 },
        lifespan: 4000,
        frequency: 150,
        tint: 0x555577,
      });
      this.overlayContainer.add(p);
    }

    // ── Title ────────────────────────────────────────────────────────────
    const titleText = data.won ? 'Level Clear!' : (data.reason === 'deadlock' ? 'Frozen Out!' : 'Game Over');
    const titleColor = data.won ? '#2ecc71' : (data.reason === 'deadlock' ? '#9b59b6' : '#e74c3c');

    const title = crispText(this.add.text(centerX, centerY - 160, titleText, {
      fontFamily: FONT_FAMILY,
      fontSize: '84px',
      color: titleColor,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
    })).setOrigin(0.5).setAlpha(0).setScale(0.8);
    this.overlayContainer.add(title);

    this.tweens.add({
      targets: title,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 400,
      ease: 'Back.easeOut',
    });

    // Look up level name from metadata
    const levelMeta = data.level >= 1 && data.level <= LEVEL_COUNT
      ? LEVEL_METADATA[data.level - 1]
      : undefined;
    const subtitle = levelMeta
      ? `Level ${data.level} — ${levelMeta.name}`
      : `Level ${data.level}`;
    const subText = crispText(this.add.text(centerX, centerY - 40, subtitle, {
      fontFamily: FONT_FAMILY,
      fontSize: '40px',
      color: '#8888aa',
    })).setOrigin(0.5);
    this.overlayContainer.add(subText);

    // Show next level name on win
    const nextMeta = data.level < LEVEL_COUNT ? LEVEL_METADATA[data.level] : undefined;
    if (data.won && nextMeta) {
      const nextText = crispText(this.add.text(centerX, centerY + 30, `Next: ${nextMeta.name}`, {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: '#aaaacc',
      })).setOrigin(0.5);
      this.overlayContainer.add(nextText);
    }

    // Helper: stop paused GameScene before navigating away
    const stopGameScene = () => {
      if (this.scene.isActive('GameScene') || this.scene.isPaused('GameScene')) {
        this.scene.stop('GameScene');
      }
    };

    // ── Buttons ──────────────────────────────────────────────────────────
    let nextY = centerY + 120;

    if (data.won) {
      createButton(this, {
        x: centerX, y: nextY, w: 400, h: 100,
        label: 'NEXT LEVEL', color: 0x2ecc71, hoverColor: 0x27ae60,
        container: this.overlayContainer,
        onClick: () => { stopGameScene(); launchGameScene(this, { level: data.level + 1, queueOverride: data.queueOverride }); },
      });
      nextY += 136;
    }

    createButton(this, {
      x: centerX, y: nextY, w: 400, h: 100,
      label: 'RETRY', color: 0x3498db, hoverColor: 0x2980b9,
      container: this.overlayContainer,
      onClick: () => { stopGameScene(); launchGameScene(this, { level: data.level, queueOverride: data.queueOverride }); },
    });
    nextY += 136;

    createButton(this, {
      x: centerX, y: nextY, w: 400, h: 100,
      label: 'LEVELS', color: 0x555577, hoverColor: 0x444466,
      container: this.overlayContainer,
      onClick: () => {
        stopGameScene();
        fadeToScene(this, () => this.scene.start('LevelSelectScene'));
      },
    });
    nextY += 136;

    createButton(this, {
      x: centerX, y: nextY, w: 400, h: 100,
      label: 'MENU', color: 0x555577, hoverColor: 0x444466,
      container: this.overlayContainer,
      onClick: () => {
        stopGameScene();
        fadeToScene(this, () => this.scene.start('MenuScene'));
      },
    });

    // ── LOG download button (top-left, always on top) ──────────────
    if (data.decisionLog && data.decisionLog.length > 0) {
      const logW = 100;
      const logH = 44;
      const logX = 32;
      const logY = 32;
      const logR = 10;

      // Separate container NOT inside overlayContainer — always visible & on top
      const logContainer = this.add.container(logX, logY).setDepth(9999);

      const logBg = this.add.graphics();
      logBg.fillStyle(0x669933, 1);
      logBg.fillRoundedRect(0, 0, logW, logH, logR);
      logContainer.add(logBg);

      const logLabel = crispText(this.add.text(logW / 2, logH / 2 + 1, 'LOG', {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
      }), false).setOrigin(0.5, 0.5);
      logContainer.add(logLabel);

      const logHit = this.add.rectangle(logW / 2, logH / 2, logW, logH, 0x000000, 0);
      logHit.setInteractive({ useHandCursor: true });
      logContainer.add(logHit);

      const capturedLog = data.decisionLog;
      const capturedLevel = data.level;
      const capturedName = data.levelName ?? 'unknown';

      logHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        const header = `pxFlow Decision Log — Level ${capturedLevel}: ${capturedName}\n` +
          `Tick | Actor  | Source       | Color(ammo)          | State\n` +
          `${'—'.repeat(90)}\n`;
        const text = header + capturedLog.join('\n') + '\n';
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `decision-log-lv${capturedLevel}-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    }

    // ── "Tap to peek" hint ──────────────────────────────────────────────
    const hintText = crispText(this.add.text(centerX, gameH - 50, 'tap background to peek at board', {
      fontFamily: FONT_FAMILY,
      fontSize: '26px',
      color: '#666688',
    }), false).setOrigin(0.5).setAlpha(0);
    this.overlayContainer.add(hintText);

    // Fade in hint after a short delay
    this.time.delayedCall(800, () => {
      this.tweens.add({ targets: hintText, alpha: 0.7, duration: 400 });
    });

    // ── Dismiss overlay on backdrop tap ─────────────────────────────────
    backdrop.on('pointerdown', () => {
      this.dismissOverlay();
    });

    // ── "Show Results" pill (hidden initially) ──────────────────────────
    this.showResultsPill = this.createResultsPill(data.won);
    this.showResultsPill.setVisible(false).setAlpha(0);

    this.overlayVisible = true;

    // Fade-in effect via camera alpha tween on the overlay
    this.overlayContainer.setAlpha(0);
    this.tweens.add({
      targets: this.overlayContainer,
      alpha: 1,
      duration: 300,
      onComplete: () => {
        // Auto-download trace and decision log AFTER overlay is fully visible.
        // Delayed from GameScene to prevent Android browsers from losing context
        // when a download prompt fires mid-animation.
        this.autoDownloadFiles(data);
      },
    });
  }

  /** Trigger automatic file downloads for trace and decision log data. */
  private autoDownloadFiles(data: GameOverData): void {
    if (data.traceText) {
      this.downloadFile(data.traceText, `bot-trace-${Date.now()}.txt`);
    }
    if (data.decisionLog && data.decisionLog.length > 0) {
      const header = `pxFlow Decision Log — Level ${data.level}: ${data.levelName ?? 'unknown'}\n` +
        `Tick | Actor  | Source       | Color(ammo)          | State\n` +
        `${'—'.repeat(90)}\n`;
      const text = header + data.decisionLog.join('\n') + '\n';
      // Small stagger so two downloads don't collide on mobile browsers
      this.time.delayedCall(200, () => {
        this.downloadFile(text, `decision-log-lv${data.level}-${Date.now()}.txt`);
      });
    }
  }

  /** Create a text file download via anchor click. */
  private downloadFile(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Dismiss the overlay to reveal the game board underneath. */
  private dismissOverlay(): void {
    if (!this.overlayVisible) return;
    this.overlayVisible = false;

    this.tweens.add({
      targets: this.overlayContainer,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.overlayContainer.setVisible(false);
        // Show the pill
        this.showResultsPill.setVisible(true);
        this.tweens.add({
          targets: this.showResultsPill,
          alpha: 1,
          duration: 200,
        });
      },
    });
  }

  /** Restore the overlay. */
  private restoreOverlay(): void {
    if (this.overlayVisible) return;
    this.overlayVisible = true;

    // Hide pill
    this.tweens.add({
      targets: this.showResultsPill,
      alpha: 0,
      duration: 150,
      onComplete: () => {
        this.showResultsPill.setVisible(false);
      },
    });

    // Show overlay
    this.overlayContainer.setVisible(true);
    this.tweens.add({
      targets: this.overlayContainer,
      alpha: 1,
      duration: 200,
      ease: 'Power2',
    });
  }

  /** Create the small floating pill button to restore the overlay. */
  private createResultsPill(won: boolean): Phaser.GameObjects.Container {
    const pillW = 280;
    const pillH = 64;
    const pillX = GAME_WIDTH / 2;
    const pillY = getGameHeight() - 50;
    const pillColor = won ? 0x2ecc71 : 0xe74c3c;

    const container = this.add.container(pillX, pillY);

    // Pill background
    const gfx = this.add.graphics();
    gfx.fillStyle(0x000000, 0.5);
    gfx.fillRoundedRect(-pillW / 2 + 3, -pillH / 2 + 3, pillW, pillH, pillH / 2);
    gfx.fillStyle(pillColor, 0.9);
    gfx.fillRoundedRect(-pillW / 2, -pillH / 2, pillW, pillH, pillH / 2);
    container.add(gfx);

    // Label
    const label = crispText(this.add.text(0, 0, 'Show Results', {
      fontFamily: FONT_FAMILY,
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    container.add(label);

    // Hit area
    const hit = this.add.rectangle(0, 0, pillW, pillH, 0x000000, 0);
    hit.setInteractive({ useHandCursor: true });
    container.add(hit);

    hit.on('pointerdown', () => {
      this.restoreOverlay();
    });

    // Gentle bob animation
    this.tweens.add({
      targets: container,
      y: pillY - 4,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return container;
  }
}
